import type { BootstrapData } from '../types/common';

const normalizeRestBase = (restUrl: string): string => {
  return restUrl.endsWith('/') ? restUrl : `${restUrl}/`;
};

export function getBootstrapData(key: string): BootstrapData {
  const data = window[key as keyof Window & string] as BootstrapData | undefined;
  if (!data) {
    throw new Error(`Bootstrap data "${key}" not found`);
  }
  return {
    ...data,
    restUrl: normalizeRestBase(data.restUrl),
  };
}

const GENERIC_ERROR = 'Une erreur inattendue est survenue';

function isTechnicalMessage(msg: string): boolean {
  return /HTTP\s*\d{3}|^\d{3}\s|status|error\s*code|WP_Error/i.test(msg);
}

export function handleApiError(error: unknown): string {
  if (error instanceof Error) {
    return isTechnicalMessage(error.message) ? GENERIC_ERROR : error.message;
  }
  if (typeof error === 'string') {
    return isTechnicalMessage(error) ? GENERIC_ERROR : error;
  }
  return GENERIC_ERROR;
}

export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
  bootstrapKey: string,
): Promise<T> {
  const bootstrap = getBootstrapData(bootstrapKey);
  const url = `${bootstrap.restUrl}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'X-WP-Nonce': bootstrap.nonce,
  };

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const msg =
      (errorData as { message?: string }).message ||
      (errorData as { error?: string }).error;
    throw new Error(msg && !isTechnicalMessage(msg) ? msg : GENERIC_ERROR);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return undefined as T;
  }

  const text = await response.text();
  if (!text || text.trim() === '') {
    return undefined as T;
  }

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return undefined as T;
  }

  const parsed = data as { success?: boolean; data?: T; error?: string; message?: string };
  if (parsed.success === false) {
    const msg = parsed.error || parsed.message;
    throw new Error(msg && !isTechnicalMessage(msg) ? msg : GENERIC_ERROR);
  }

  return (parsed.data ?? data) as T;
}
